import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './styles/globals.css';
import HomePage           from './pages/HomePage';
import ServicesPage       from './pages/ServicesPage';
import AboutPage          from './pages/AboutPage';
import ContactPage        from './pages/ContactPage';
import PrivacyPage        from './pages/PrivacyPage';
import TermsPage          from './pages/TermsPage';
import TrainingAdminPage  from './pages/TrainingAdminPage';
import ReviewsPage        from './pages/ReviewsPage';
import CommunityPage      from './pages/CommunityPage';
import AdminDashboard     from './pages/AdminDashboard';
import AdminReviewsPage   from './pages/AdminReviewsPage';
import AdminCommunityPage from './pages/AdminCommunityPage';
import AstrologerPage    from './pages/AstrologerPage';
import AppDownloadPage   from './pages/AppDownloadPage';
import InstallPage       from './pages/InstallPage';
import OnboardingSlides   from './components/onboarding/OnboardingSlides';
import UpdateBanner       from './components/common/UpdateBanner';
import CallProvider       from './components/call/CallProvider';
import CallOverlay        from './components/call/CallOverlay';
import NotificationPrompt from './components/common/NotificationPrompt';
import useVapidPush       from './hooks/useVapidPush';

// ── Role-based access (v2) — partially scaffolded, page components not yet built.
// AuthContext/ProtectedRoute exist but LoginPage/SignupPage/Dashboards do not.
// Re-enable when those files exist under pages/rbac/.
// import { AuthProvider }    from './auth/AuthContext';
// import ProtectedRoute      from './auth/ProtectedRoute';
// import RoleRedirect        from './auth/RoleRedirect';
// import LoginPage           from './pages/rbac/LoginPage';
// import SignupPage          from './pages/rbac/SignupPage';
// import UserDashboard       from './pages/rbac/UserDashboard';
// import AstrologerDashboard from './pages/rbac/AstrologerDashboard';
// import AdminDashboard      from './pages/rbac/AdminDashboard';

function App() {
  // First-install onboarding flow.
  // - Browser: show once until 'vtd_onboarded' is set.
  // - PWA (start_url has ?from=pwa): show once on first PWA launch even if the
  //   user already saw it in the browser. Tracked separately as 'vtd_pwa_onboarded'.
  // - /?onboard=1 forces a replay (admin/testing).
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('onboard') === '1') {
        localStorage.removeItem('vtd_onboarded');
        localStorage.removeItem('vtd_pwa_onboarded');
        return true;
      }
      const isPwaLaunch =
        params.get('from') === 'pwa' ||
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      if (isPwaLaunch && !localStorage.getItem('vtd_pwa_onboarded')) {
        return true;
      }
      const hasSeenOnboarding = localStorage.getItem('vtd_onboarded');
      return !hasSeenOnboarding;
    } catch (_) {
      return true;
    }
  });

  // Stable user ID for push notifications and call provider
  const [userId] = useState(() => {
    try {
      let id = localStorage.getItem('vtd_user_id');
      if (!id) { id = `user_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem('vtd_user_id', id); }
      return id;
    } catch (_) { return `user_${Date.now()}`; }
  });

  // Initialize VAPID push notifications
  const { subscribe: vapidSubscribe } = useVapidPush(userId);
  const handleEnableNotifications = useCallback(() => { vapidSubscribe(); }, [vapidSubscribe]);

  if (showOnboarding) {
    return <OnboardingSlides onComplete={() => {
      try {
        localStorage.setItem('vtd_onboarded', 'true');
        localStorage.setItem('vtd_pwa_onboarded', 'true');
      } catch (_) {}
      setShowOnboarding(false);
    }} />;
  }

  return (
    <CallProvider userId={userId} userType="user">
      <Router>
        <UpdateBanner />
        <CallOverlay />
        <NotificationPrompt onSubscribe={handleEnableNotifications} />
        <Routes>
          {/* ── Public pages ── */}
          <Route path="/"          element={<HomePage />} />
          <Route path="/services"  element={<ServicesPage />} />
          <Route path="/about"     element={<AboutPage />} />
          <Route path="/contact"   element={<ContactPage />} />
          <Route path="/privacy"   element={<PrivacyPage />} />
          <Route path="/terms"     element={<TermsPage />} />
          <Route path="/reviews"   element={<ReviewsPage />} />
          <Route path="/community" element={<CommunityPage />} />

          {/* ── PWA install (static build/appdownload in prod; redirect in dev) ── */}
          <Route path="/appdownload" element={<AppDownloadPage />} />
          <Route path="/install"     element={<InstallPage />} />

          {/* ── Astrologer Tool ── */}
          <Route path="/astrologer" element={<AstrologerPage />} />

          {/* ── Admin ── */}
          <Route path="/admin"           element={<AdminDashboard />} />
          <Route path="/admin/training"  element={<TrainingAdminPage />} />
          <Route path="/admin/reviews"   element={<AdminReviewsPage />} />
          <Route path="/admin/community" element={<AdminCommunityPage />} />

          {/* ── Role-based auth + dashboards — re-enable when rbac pages exist ──
          <Route path="/login"     element={<LoginPage />} />
          <Route path="/signup"    element={<SignupPage />} />
          <Route path="/dashboard" element={<RoleRedirect />} />
          <Route path="/me"        element={<ProtectedRoute roles={['user', 'admin']}><UserDashboard /></ProtectedRoute>} />
          <Route path="/pro-desk"  element={<ProtectedRoute roles={['astrologer', 'admin']}><AstrologerDashboard /></ProtectedRoute>} />
          <Route path="/admin"     element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
          */}

          {/* ── Unknown routes → home (nginx serves /astrologer AI tool + other backend paths before React loads) ── */}
          <Route path="*" element={<HomePage />} />
        </Routes>
      </Router>
    </CallProvider>
  );
}

export default App;
